/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * This function check the status of shouldSetError variable
 * and invoke set or remove on the basis of status
 *
 * @param sourceControl : conation AbstractControl of the form
 * @param shouldSetError : contain boolean value to deicide to invoke setError or removeError
 * @param errorKey : contain error key
 */
export function checkAndSetError(
  sourceControl: AbstractControl,
  shouldSetError: boolean,
  errorKey: string
): void {
  if (shouldSetError) {
    setError(errorKey, sourceControl);
  } else {
    removeError(errorKey, sourceControl);
  }
}

/**
 * This function set error to form control
 *
 * @param errorKey : contain error key
 * @param control : conation AbstractControl of the form
 */
export function setError(errorKey: string, control: AbstractControl): void {
  control.setErrors({ [errorKey]: true, ...control.errors });
}

/**
 * This function set error to form control
 *
 * @param errorKey : contain error key
 * @param control : conation AbstractControl of the form
 */
export function removeError(errorKey: string, control: AbstractControl): void {
  if (control.errors) {
    delete control.errors[errorKey];
    if (Object.keys(control.errors)?.length === 0) {
      control.setErrors(null);
    }
  }
}

/**
 * Retrieves the form control associated with the specified key.
 *
 * @param form
 * @param {string} key - The key associated with the form control.
 * @returns {AbstractControl | undefined} - The form control if found, otherwise undefined.
 */
export function getFormControl(
  form: FormGroup,
  key: string
): AbstractControl | undefined {
  return form.get([key]) as AbstractControl;
}

export function disableControls(
  form: FormGroup,
  keys: string[] | string
): void {
  mutateControl(form, keys, 'disable');
}

export function resetControls(form: FormGroup, keys: string[] | string): void {
  mutateControl(form, keys, 'reset');
}

export function enableControls(form: FormGroup, keys: string[] | string): void {
  mutateControl(form, keys, 'enable');
}

function mutateControl(
  form: FormGroup,
  keys: string[] | string,
  action: string
): void {
  if (typeof keys === 'string') {
    const control = getFormControl(form, keys);
    if (control) handleMutation(control, action);
    return;
  }
  for (const key of keys) {
    const control = getFormControl(form, key);
    if (control) handleMutation(control, action);
  }
}

function handleMutation(control: AbstractControl, action: string): void {
  switch (action) {
    case 'enable':
      control.enable();
      return;
    case 'disable':
      control.disable();
      return;
    case 'reset':
      control.reset();
      return;
    default:
      return;
  }
}

export function showValidationMsg(formGroup: FormGroup): void {
  for (const key in formGroup.controls) {
    if (Object.hasOwnProperty.call(formGroup.controls, key)) {
      const control: FormControl = formGroup.controls[key] as FormControl;
      if (Object.keys(control).includes('controls')) {
        const formGroupChild: FormGroup = formGroup.controls[key] as FormGroup;
        showValidationMsg(formGroupChild);
      }
      control.markAsTouched();
    }
  }
}

type Assert = (
  control: AbstractControl | null,
  controlName: string
) => asserts control;
export const assertControlName: Assert = (
  control: AbstractControl | null,
  controlName: string
): asserts control is AbstractControl => {
  if (!control) {
    throw new Error(
      `Control wil name: ${controlName} has not been registered into form group`
    );
  }
};

export function ConfirmPasswordValidator(
  controlName: string,
  matchingControlName: string
): ValidationErrors | null {
  return (formGroup: FormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];
    if (
      matchingControl.errors &&
      !matchingControl.errors['confirmPasswordValidator']
    ) {
      return;
    }
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ confirmPasswordValidator: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}

/**
 * Validates that a control's value is parseable JSON. Empty values pass
 * (leave emptiness to Validators.required); malformed JSON yields invalidJson.
 */
export const jsonValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (value === '' || value === null || value === undefined) return null;
  try {
    JSON.parse(value);
    return null;
  } catch {
    return { invalidJson: true };
  }
};

export function removeEmptyStrings(obj: any): any {
  const clone = { ...obj };
  Object.entries(clone).forEach(
    ([key, val]) =>
      (val === '' || val === null || val === undefined) && delete clone[key]
  );
  return clone;
}
